import React, { useState, useEffect } from "react";
import { useGlobalState } from "../contexts/GlobalContext";
import { useNavigate } from "react-router-dom";
import { FaSave, FaTimes } from "react-icons/fa";
import { formatPhoneNumberInput } from '../utils/formatters';
import { apiFetch } from "../utils/api";
import Snackbar from "./Snackbar";


function EditProfile() {
  const { state, dispatch } = useGlobalState();
  const navigate = useNavigate();
  const user = state.user || {};
  

  const [formData, setFormData] = useState({
    userId: "", firstName: "", lastName: "", email: "", cellPhone: "",
    location: "", profilePicture: "", licenseNumber: "",
    facebook: "", linkedin: "", x: "",
    bio: "", specializations: [],
  });
  const [snackbar, setSnackbar] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    if (user) {
      setFormData({
        userId: user.userId || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        cellPhone: formatPhoneNumberInput(user.cellPhone || ""),
        location: user.location || "",
        profilePicture: user.profilePicture || "",
        licenseNumber: user.licenseNumber || "",
        bio: user.bio || "",
        facebook: user.facebook || "",
        linkedin: user.linkedin || "",
        x: user.x || "",
        specializations: user.specializations || [],
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === "cellPhone" ? formatPhoneNumberInput(value) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      const result = await apiFetch('editUser', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      

      if (!result.success) {
        setSnackbar({ show: true, message: `❌ ${result.message}`, type: "danger" });
        return;
      }

      dispatch({ type: "UPDATE_USER_PROFILE", payload: formData });
      setSnackbar({ show: true, message: "✅ Profile updated!", type: "success" });
      setTimeout(() => navigate(`/profile/${formData.userId}`), 1200);
    } catch (err) {
      console.error("Error saving profile:", err);
      setSnackbar({ show: true, message: "❌ Error saving profile.", type: "danger" });
    }
  };

  return (
    <>
    <Snackbar
      show={snackbar.show}
      message={snackbar.message}
      type={snackbar.type}
      onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
    />
    <form onSubmit={handleSave} className="container bg-light shadow-sm rounded p-3">
      {/* Name & Contact Info */}
      <div className="row g-2">
        {[
          { label: "First Name", name: "firstName" },
          { label: "Last Name", name: "lastName" },
          { label: "Email", name: "email" },
          { label: "Cell Phone", name: "cellPhone" },
        ].map(({ label, name }, idx) => (
          <div className="col-md-3" key={idx}>
            <input type="text" className="form-control form-control-sm" placeholder={label}
              name={name} value={formData[name]} onChange={handleChange} required />
          </div>
        ))}
      </div>

      {/* Location & License */}
      <div className="row g-2 mt-2">
        {[
          { label: "Location", name: "location" },
          { label: "License Number", name: "licenseNumber" },
          { label: "Profile Picture", name: "profilePicture" },
        ].map(({ label, name }, idx) => (
          <div className="col-md-3" key={idx}>
            <input type="text" className="form-control form-control-sm" placeholder={label}
              name={name} value={formData[name]} onChange={handleChange} />
          </div>
        ))}
      </div>

      {/* Bio */}
      <div className="row g-2 mt-2">
        <div className="col-md-12">
          <textarea className="form-control form-control-sm" rows="3" placeholder="Enter a short bio..."
            name="bio" value={formData.bio} onChange={handleChange} />
        </div>
      </div>

      {/* Social Links */}
      <div className="row g-2 mt-2">
        {["facebook", "linkedin", "x"].map((platform, idx) => (
          <div className="col-md-4" key={idx}>
            <input type="text" className="form-control form-control-sm" placeholder={`${platform.charAt(0).toUpperCase() + platform.slice(1)} URL`}
              name={platform} value={formData[platform]} onChange={handleChange} />
          </div>
        ))}
      </div>

      <div className="row g-2 mt-2">
        <div className="col-md-12">
          <label className="form-label">Specializations</label>
          <select className="form-select form-select-sm" multiple
            name="specializations" value={formData.specializations} onChange={(e) =>
              setFormData({ ...formData, specializations: [...e.target.selectedOptions].map(o => o.value) })}>

            <optgroup label="Residential">
              <option value="Residential">Residential</option>
              <option value="Luxury Homes">Luxury Homes</option>
              <option value="Single-Family Homes">Single-Family Homes</option>
              <option value="Multi-Family Homes">Multi-Family Homes</option>
              <option value="First-Time Buyers">First-Time Buyers</option>
              <option value="Condos & Townhomes">Condos & Townhomes</option>
            </optgroup>

            <optgroup label="Investment & Commercial">
              <option value="Commercial">Commercial Properties</option>
              <option value="Investment Properties">Investment Properties</option>
              <option value="Retail Properties">Retail Properties</option>
              <option value="Industrial Properties">Industrial Properties</option>
              <option value="Mixed-Use Developments">Mixed-Use Developments</option>
            </optgroup>

            <optgroup label="Land & Development">
              <option value="Land">Land Sales</option>
              <option value="Agricultural & Farm Land">Agricultural & Farm Land</option>
              <option value="New Construction & Development">New Construction & Development</option>
              <option value="Vacant Land">Vacant Land</option>
            </optgroup>

            <optgroup label="Luxury & High-End">
              <option value="High-End Luxury Properties">High-End Luxury Properties</option>
              <option value="Waterfront Properties">Waterfront Properties</option>
              <option value="Historic Homes">Historic Homes</option>
              <option value="Golf Course Homes">Golf Course Homes</option>
            </optgroup>

            <optgroup label="Niche Markets">
              <option value="Relocation Services">Relocation Services</option>
              <option value="Military & Veterans">Military & Veterans</option>
              <option value="Vacation & Short-Term Rentals">Vacation & Short-Term Rentals</option>
              <option value="Senior Living & Retirement Communities">Senior Living & Retirement Communities</option>
            </optgroup>

          </select>
        </div>
      </div>

      {/* Save & Cancel Buttons */}
      <div className="d-flex justify-content-center mt-3">
        <button type="submit" className="btn btn-primary btn-sm me-2">
          <FaSave className="me-1" /> Save Changes
        </button>
        <button className="btn btn-outline-danger btn-sm" onClick={() => navigate("/profile")}>
          <FaTimes /> Cancel
        </button>
      </div>
    </form>
    </>
  );
}

export default EditProfile;
